import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Fade,
  Slide,
  Avatar,
  Button,
  Container,
  CircularProgress,
  Chip,
} from '@mui/material';
import { ArrowBack, SmartToy, Memory } from '@mui/icons-material';
import { Project, Compute } from '../types';
import { computesAPI } from '../services/api';

interface RobotDetailsPageProps {
  robot: Project;
  onBack: () => void;
  onSelectCompute: (compute: Compute) => void;
}

const RobotDetailsPage: React.FC<RobotDetailsPageProps> = ({
  robot,
  onBack,
  onSelectCompute,
}) => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [computes, setComputes] = useState<Compute[]>([]);
  const [loading, setLoading] = useState(true);

  // Map compute types to display info
  const getComputeInfo = (computeType: string) => {
    const infoMap: Record<string, { name: string; gradient: string; iconColor: string }> = {
      'agx': {
        name: 'AGX',
        iconColor: '#8b5cf6',
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      },
      'nx': {
        name: 'NX',
        iconColor: '#22c55e',
        gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
      },
      'nano-rs': {
        name: 'NANO-RS',
        iconColor: '#3b82f6',
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      },
    };
    return infoMap[computeType.toLowerCase()] || {
      name: computeType.toUpperCase(),
      iconColor: '#64748b',
      gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
    };
  };

  useEffect(() => {
    const fetchComputes = async () => {
      try {
        setLoading(true);
        // Try fetching computes by project name first (for backward compatibility)
        let response = await computesAPI.getComputes({
          projectName: robot.projectName,
        });
        
        // If no computes found, try by IP address
        if (!response.success || !response.data || response.data.length === 0) {
          // Note: We might need to first find the robot by IP/name, then get computes
          // For now, we'll use projectName as a fallback
          response = await computesAPI.getComputes({
            projectName: robot.projectName,
          });
        }
        
        if (response.success && response.data) {
          setComputes(response.data);
        }
      } catch (error) {
        console.error('Error fetching computes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchComputes();
  }, [robot]);

  const renderComputeCard = (
    compute: Compute,
    index: number
  ) => {
    const computeInfo = getComputeInfo(compute.computeType);
    return (
    <Slide direction="up" in timeout={600 + index * 200} mountOnEnter unmountOnExit>
      <Card
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          color: '#334155',
          cursor: 'pointer',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-12px) scale(1.02)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          },
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
          minHeight: 320,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }}
        onMouseEnter={() => setHoveredCard(compute._id)}
        onMouseLeave={() => setHoveredCard(null)}
        onClick={() => onSelectCompute(compute)}
      >
        <CardContent sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 3,
              background: computeInfo.gradient,
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              transform: hoveredCard === compute._id ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
              transition: 'all 0.3s ease',
            }}
          >
            <Memory sx={{ fontSize: 40, color: 'white' }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" mb={2} sx={{ color: '#1a202c' }}>
            {computeInfo.name}
          </Typography>
          {compute.status && (
            <Chip
              label={compute.status.toUpperCase()}
              size="small"
              sx={{
                mb: 2,
                backgroundColor: compute.status === 'active' ? '#22c55e' : '#ef4444',
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          )}
          {compute.gitLogsCount !== undefined && (
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              {compute.gitLogsCount} commits
            </Typography>
          )}
          {compute.branchName && (
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
              Branch: {compute.branchName}
            </Typography>
          )}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              background: computeInfo.gradient,
              color: 'white',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              },
            }}
          >
            View Details
          </Box>
        </CardContent>
      </Card>
    </Slide>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(139, 92, 246, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%)
          `,
          zIndex: 1,
        },
        display: 'flex',
        flexDirection: 'column',
        p: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Fade in timeout={500}>
          <Box sx={{ mb: 6 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{
                mb: 4,
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              Back to Robot List
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  mr: 2,
                }}
              >
                <SmartToy sx={{ fontSize: 32, color: '#22c55e' }} />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ color: 'white' }}>
                  {robot.customName || (robot.robotNames && robot.robotNames.length > 0 ? robot.robotNames[0] : robot.projectName)}
                </Typography>
                {robot.ipAddress && (
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
                    IP Address: {robot.ipAddress}
                  </Typography>
                )}
              </Box>
            </Box>

            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 4, textAlign: 'center' }}>
              Select a compute module to view details and manage this robot
            </Typography>
          </Box>
        </Fade>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        ) : computes.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 4,
              maxWidth: 1200,
              width: '100%',
              mx: 'auto',
            }}
          >
            {computes.map((compute, index) => renderComputeCard(compute, index))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              No computes found for this robot
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 2 }}>
              Computes will appear here once data is synced from the robot
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default RobotDetailsPage;

